/*============================================================================
Gift Event Logger and Visibility Controller
==============================================================================

This script manages the visibility of gift-related messages in the shopping cart drawer.
It handles the following scenarios:
1. Shows/hides gift messages based on gift availability
2. Maintains visibility during rapid cart updates
3. Provides a "safe window" to keep messages visible after gift events
4. Handles cart drawer open/close events
5. Manages smooth animations with debouncing

Key Components:
- Gift messages visibility control
- Cart drawer state observation
- Animation management with cooldown
- Debug logging system
- Event handling for cart updates and gift events

Author: Jakob Albrecht
Version: 1.0.0
Last Updated: 2024-03-21
==============================================================================*/

(() => {
  // Configuration object for easy adjustments
  const CONFIG = {
    DEBUG: false, // Enable/disable debug logging
    ANIMATION_COOLDOWN: 500, // Minimum time (ms) between animations to prevent flicker
    GIFT_CHECK_TIMEOUT: 2000, // Time (ms) to wait for gift event before hiding messages
    ANIMATION_DURATION: 500, // Duration (ms) of show/hide animations
    ANIMATION_DELAY: 50 // Delay (ms) before starting show animation
  };

  // Central state management for tracking various conditions
  const state = {
    giftEventTimeout: null, // Timeout handle for gift check
    giftEventFired: false, // Flag indicating if gift event occurred
    lastGiftEventTime: 0, // Timestamp of last gift event
    lastAnimationTime: 0 // Timestamp of last animation start
  };

  /**
   * Conditional logging utility
   * @param {string} message - Message to log
   * @param {string} emoji - Optional emoji prefix
   */
  const log = (message, emoji = '') => {
    if (CONFIG.DEBUG) {
      console.log(`${emoji} ${message}`);
    }
  };

  /**
   * Updates the visibility of gift-related elements with animation
   * @param {boolean} hasGift - Whether to show (true) or hide (false) the elements
   */
  const updateGiftVisibility = (hasGift) => {
    // Select all relevant gift message elements
    const dividers = document.querySelectorAll('.bogos-yellow-divider');
    const messageWrappers = document.querySelectorAll(
      '.cart-message-angebot--main-wrapper, .drawer-cart-message-angebot--main-wrapper'
    );
    
    const now = Date.now();
    // Prevent animation spam by checking cooldown period
    const shouldAnimate = now - state.lastAnimationTime > CONFIG.ANIMATION_COOLDOWN;

    [].concat(...dividers, ...messageWrappers).forEach(el => {
      if (!el) return;

      const isYellowDivider = el.classList.contains('bogos-yellow-divider');
      const displayStyle = isYellowDivider ? 'block' : 'flex';
      
      if (hasGift) {
        // Show elements with animation if needed
        if (!shouldAnimate && el.style.opacity === '1') return;

        el.style.removeProperty('display');
        el.style.display = displayStyle;
        
        if (shouldAnimate) {
          // Animate showing elements
          el.style.opacity = '0';
          el.style.transform = 'translateY(20px)';
          el.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
          
          setTimeout(() => {
            el.style.opacity = '1';
            el.style.transform = 'translateY(0)';
          }, CONFIG.ANIMATION_DELAY);
          
          state.lastAnimationTime = now;
        } else {
          // Direct state change without animation
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
        }
      } else {
        // Hide elements with animation if needed
        if (!shouldAnimate && el.style.display === 'none') return;

        if (shouldAnimate) {
          // Animate hiding elements
          el.style.opacity = '0';
          el.style.transform = 'translateY(20px)';
          el.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
          
          setTimeout(() => {
            el.style.display = 'none';
          }, CONFIG.ANIMATION_DURATION);
          
          state.lastAnimationTime = now;
        } else {
          // Direct state change without animation
          el.style.display = 'none';
        }
      }
    });
  };

  /**
   * Checks for gift events and manages message visibility
   * @param {boolean} isDrawerOpen - Whether this check is triggered by drawer opening
   */
  const checkGiftEvent = (isDrawerOpen = false) => {
    clearTimeout(state.giftEventTimeout);

    // Safe window check: keep messages visible if drawer opens shortly after gift event
    if (isDrawerOpen && Date.now() - state.lastGiftEventTime < CONFIG.GIFT_CHECK_TIMEOUT) {
      log('Safe window active - keeping gift messages visible', '🛡️');
      state.giftEventFired = true;
      updateGiftVisibility(true);
      return;
    }

    // Start new gift check cycle
    state.giftEventFired = false;
    
    state.giftEventTimeout = setTimeout(() => {
      if (!state.giftEventFired) {
        log('KEIN GESCHENK', '❌');
        updateGiftVisibility(false);
      }
    }, CONFIG.GIFT_CHECK_TIMEOUT);
  };

  /**
   * Sets up MutationObserver for cart drawer to detect open/close events
   */
  const setupCartDrawerObserver = () => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      log('Cart drawer found', '🎯');
      
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          // Check for relevant attribute changes
          if (mutation.type === 'attributes' && 
              (mutation.attributeName === 'open' || mutation.attributeName === 'active')) {
            if (cartDrawer.hasAttribute('open') && cartDrawer.hasAttribute('active')) {
              log('Drawer open', '🚪');
              checkGiftEvent(true);
            }
          }
        });
      });

      observer.observe(cartDrawer, {
        attributes: true
      });
      
      log('Observer setup complete', '👀');
    } else {
      // Retry setup if cart drawer isn't available yet
      log('Cart drawer not found, retrying...', '🔄');
      setTimeout(setupCartDrawerObserver, CONFIG.ANIMATION_COOLDOWN);
    }
  };

  // Initialize observer when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupCartDrawerObserver);
  } else {
    setupCartDrawerObserver();
  }

  // Event Listeners
  // Gift slider event - shows messages when gift is available
  document.addEventListener("fg-gifts:show-slider", () => {
    clearTimeout(state.giftEventTimeout);
    state.giftEventFired = true;
    state.lastGiftEventTime = Date.now();
    log('GESCHENK ✅');
    updateGiftVisibility(true);
  });

  // Cart update event - checks gift status on cart changes
  document.addEventListener('cart:updated', () => checkGiftEvent(false));

  // Page load event - initial gift status check
  document.addEventListener('page:loaded', () => checkGiftEvent(false));
})();